import { useQuery } from "@tanstack/react-query";
import { CommunicationContentKind } from "@api/shared-runtime/enums";
import { AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useTRPC } from "@/trpc";

const EMAIL_FRAME_CLASS_NAME =
  "h-[65dvh] w-full rounded-xl border border-slate-200 bg-white";

export type CommunicationContentTarget = {
  notificationDeliveryId: string;
  method: string | null;
  category: string | null;
  sentAtLabel: string;
};

type CommunicationContentDialogProps = {
  userId: string;
  target: CommunicationContentTarget | null;
  onClose: () => void;
};

const contentQueryOptions = {
  trpc: { context: { skipBatch: true } },
};

function CommunicationContentSkeleton() {
  return (
    <div role="status" aria-busy="true" className="space-y-3">
      <span className="sr-only">Loading message content</span>
      <Skeleton aria-hidden="true" className="h-5 w-2/3" />
      <Skeleton aria-hidden="true" className="h-4 w-1/3" />
      <Skeleton aria-hidden="true" className="h-[50dvh] w-full rounded-xl" />
    </div>
  );
}

function CommunicationContentBody({
  userId,
  notificationDeliveryId,
}: {
  userId: string;
  notificationDeliveryId: string;
}) {
  const trpc = useTRPC();
  const contentQuery = useQuery(
    trpc.adminDashboard.getUserCommunicationContent.queryOptions(
      { userId, notificationDeliveryId },
      contentQueryOptions,
    ),
  );

  if (contentQuery.isLoading) {
    return <CommunicationContentSkeleton />;
  }

  if (contentQuery.error || !contentQuery.data) {
    return (
      <div
        className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        role="alert"
      >
        <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <span className="[overflow-wrap:anywhere]">
          {contentQuery.error?.message ?? "Failed to load message content."}
        </span>
      </div>
    );
  }

  const content = contentQuery.data;

  if (content.kind === CommunicationContentKind.Email) {
    return (
      <div className="min-w-0 space-y-3">
        {content.subject || content.toEmail ? (
          <div className="space-y-1 text-sm">
            {content.subject ? (
              <p className="font-medium text-slate-900 [overflow-wrap:anywhere]">
                {content.subject}
              </p>
            ) : null}
            {content.toEmail ? (
              <p className="text-slate-600 [overflow-wrap:anywhere]">
                To: {content.toEmail}
              </p>
            ) : null}
          </div>
        ) : null}
        <iframe
          srcDoc={content.html}
          sandbox=""
          title="Email content"
          className={EMAIL_FRAME_CLASS_NAME}
        />
        <p className="text-xs text-slate-500">
          Re-rendered from the current template with the data stored at send
          time. Wording may have changed since, and dates the template adds
          itself show today&apos;s date.
        </p>
      </div>
    );
  }

  if (content.kind === CommunicationContentKind.Text) {
    return (
      <div className="min-w-0 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800">
        {content.title ? (
          <p className="font-semibold text-slate-900 [overflow-wrap:anywhere]">
            {content.title}
          </p>
        ) : null}
        <p className="whitespace-pre-wrap leading-6 [overflow-wrap:anywhere]">
          {content.body}
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500 [overflow-wrap:anywhere]">
      {content.reason}
    </div>
  );
}

export function CommunicationContentDialog({
  userId,
  target,
  onClose,
}: CommunicationContentDialogProps) {
  const summary = target
    ? [target.method, target.category, target.sentAtLabel]
        .filter(Boolean)
        .join(" • ")
    : "";

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="!w-[calc(100vw-2rem)] !max-w-3xl !max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="text-xl leading-7">
            Message content
          </DialogTitle>
          <DialogDescription className="text-sm capitalize leading-6 text-slate-600 [overflow-wrap:anywhere]">
            {summary}
          </DialogDescription>
        </DialogHeader>

        {target ? (
          <CommunicationContentBody
            userId={userId}
            notificationDeliveryId={target.notificationDeliveryId}
          />
        ) : null}

        <DialogFooter className="mt-0 flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:space-x-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
